import React from 'react'
import moment from 'moment'
import { UserState } from 'redux-oidc'
import { connect } from 'react-redux'

import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AxisDomain,
  Line,
  LineChart,
  Dot,
  TooltipProps,
  Legend,
  ReferenceArea,
} from 'recharts'
import { Props as DotProps } from 'recharts/src/shape/Dot'
import {
  Theme,
  createStyles,
  WithStyles,
  withStyles,
  Tooltip as MuiTooltip,
  Typography,
  Snackbar,
} from '@material-ui/core'
import { HistoricalDataEntry, updateTelemetryDatapoint } from '../../api/client'
import { TimeRange, getTimeRange } from '../misc/TimeRange'
import theme from '../../theme'
import { blue } from '@material-ui/core/colors'
import { AppState } from '../../store/AppState'
import { MultitenantUserState } from 'store/reducers/multitenant'
import {
  ProductionCode,
  TelemetryAlertViewModel,
  TelemetryType,
} from '../../api/apiservice'

import {
  hasSupervisorUserAccessLevel,
  isAdmin,
  isSystemAdmin,
} from 'store/oidc/userManager'
import { Asset, Site } from 'store/Site'
import { feetToBbl, inchToFeet } from 'components/utils/converter'
import InvalidDatapointDialog from 'components/details/InvalidData/InvalidDatapoint'
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert'
import { getStatusLensColor } from 'components/utils'
import { StatusRangeResponse, ThresholdsType } from 'api/alertservice'

const referenceColor = blue.A700

const styles = (_: Theme) =>
  createStyles({
    container: {
      height: '100%',
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: '40vw',
      },
      display: 'grid',
      alignItems: 'stretch',
    },
    responsiveContainer: {
      gridColumn: 1,
      gridRow: 1,
    },
  })

const chartRefreshRateMs = moment.duration(1, 'second').asMilliseconds()

export interface Reference {
  readonly value: number
  readonly from: Date
  readonly to: Date
}

enum LegendState {
  Show = 1,
  BringToFront = 2,
  Hide = 3,
}

export interface Props {
  readonly data: HistoricalDataEntry[]
  readonly formatValue?: (value?: any, addValue?: string) => React.ReactNode
  readonly formatRawValue?: (value?: any) => React.ReactNode
  readonly yAxisDomain?: [AxisDomain, AxisDomain]
  readonly timeRange: TimeRange
  readonly statusRanges: StatusRangeResponse[]
  readonly fullWidth?: boolean
  readonly style?: React.CSSProperties
  readonly references?: Reference[]
  readonly referenceName?: string
  readonly updateReferences?: (timeRange: TimeRange) => void
  readonly user?: MultitenantUserState
  readonly updateState: (dataModified, hiddenData) => string[]
  readonly site: Site
  readonly asset: Asset
  hideData: string[]
  readonly thresholdsType: ThresholdsType
}

interface PropsFromStateUser {
  readonly accessToken?: string
  readonly tenantId: number
  readonly email: string
}

type PropsFromState = Pick<UserState, 'user'>

type AllProps = Props &
  WithStyles<typeof styles> &
  PropsFromState &
  PropsFromStateUser

interface State {
  readonly xAxisDomain: [number, number]
  readonly legendState: LegendState
  readonly showTooltipCursor?: boolean
  readonly isUserSystemAdmin: boolean
  readonly currData?: HistoricalDataEntry
  readonly showModal: boolean
  hoverIsActive: boolean
  lastClickTime: number
  lastDatapointClicked: any
  hiddenData: string[]
  readonly updatedInfo: boolean
}

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

class SensorChartRates extends React.Component<AllProps, State> {
  private timer?: NodeJS.Timeout

  constructor(props) {
    super(props)

    this.state = {
      xAxisDomain: this.xAxisDomain,
      legendState: LegendState.Show,
      isUserSystemAdmin: false,
      hoverIsActive: false,
      currData: undefined,
      lastClickTime: 0,
      lastDatapointClicked: undefined,
      showModal: false,
      hiddenData: this.props.hideData,
      updatedInfo: false,
    }
    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.handleClick = this.handleClick.bind(this)
  }

  isSystemAdmin = isSystemAdmin(this.props.user?.selectedTenant?.roles)
  isAdmin = isAdmin(this.props.user?.selectedTenant?.roles)
  validRole = hasSupervisorUserAccessLevel(
    this.props.user?.selectedTenant?.roles
  )

  public componentDidMount() {
    const isUserSystemAdmin = this.props.user?.selectedTenant?.roles
      ? isSystemAdmin(this.props.user?.selectedTenant?.roles)
      : false
    this.timer = setInterval(
      () =>
        this.setState({
          ...this.state,
          isUserSystemAdmin,
          xAxisDomain: this.xAxisDomain,
        }),
      chartRefreshRateMs
    )
  }

  public componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  }

  public openModal = (currentData) => {
    if (currentData !== undefined) {
      this.setState({
        currData: currentData,
        showModal: true,
      })
    }
  }

  public closeModal = (id: string) => (ok: boolean) => {
    if (ok) {
      const currData = this.state.currData
      this.setValidDatapoint(
        currData?.id,
        currData?.telemetryType,
        currData?.invalidData,
        currData
      )
    } else {
      this.setState({
        showModal: false,
        currData: undefined,
      })
    }
  }

  async setValidDatapoint(
    id: any,
    telemetryType: TelemetryType | undefined,
    invalidData: boolean | undefined,
    currentData: HistoricalDataEntry | undefined
  ): Promise<void> {
    const { accessToken, tenantId, email } = this.props

    if (!accessToken || !tenantId) {
      return
    }

    const result = await updateTelemetryDatapoint(
      accessToken,
      tenantId,
      telemetryType,
      invalidData,
      email,
      id
    ) // .then((result) => {
    let currState: string[] = []
    if (result) {
      const state = currentData?.invalidData!
      const itemFound = this.state.hiddenData.findIndex((t) => t === id)
      if (itemFound !== undefined && itemFound !== -1) {
        currState = this.props.updateState(itemFound, state)
      } else {
        currState = this.props.updateState(id, true)
      }
    }
    this.setState({
      showModal: false,
      currData: undefined,
      hiddenData: currState,
      updatedInfo: true,
    })
    // })
  }

  public handleClick = (props) => {
    const currentTime = new Date().getTime()
    const lastClickTime = this.state.lastClickTime
    const diff = currentTime - lastClickTime

    const datapoint = props?.activePayload?.length
      ? props.activePayload[0]?.payload
      : undefined

    if (diff < 300 && datapoint.id === this.state.lastDatapointClicked?.id) {
      this.openModal(datapoint)
    }
    this.setState({
      lastClickTime: currentTime,
      lastDatapointClicked: datapoint,
    })
  }

  public render() {
    const { classes, fullWidth, style, references } = this.props
    const divStyle: React.CSSProperties | undefined = fullWidth
      ? { ...style, width: '100%' }
      : style
    const referencesPresent = references && references.length > 0

    return (
      <div className={classes.container} style={divStyle}>
        <ResponsiveContainer className={classes.responsiveContainer}>
          {this.renderChart()}
        </ResponsiveContainer>
        {referencesPresent && this.renderLegend()}
        {this.state.showModal && (
          <InvalidDatapointDialog
            currData={this.state.currData}
            site={this.props.site}
            onClose={this.closeModal(this.state.currData?.id)}
          />
        )}
        {this.state.updatedInfo && (
          <Snackbar
            open={true}
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            style={{ zIndex: 2147483647, top: 100 }}
            onClose={() => {
              this.setState({
                updatedInfo: false,
              })
            }}
          >
            <Alert severity="warning">
              The data has changed, please{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  window.location.reload()
                }}
                style={{
                  color: '#fff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                refresh the page
              </a>
              .
            </Alert>
          </Snackbar>
        )}
      </div>
    )
  }

  private renderChart() {
    const { yAxisDomain, statusRanges: thresholds } = this.props
    const { xAxisDomain, legendState } = this.state
    const [from, to] = xAxisDomain
    const duration = to - from
    const tickCount = moment.duration(duration).asHours() < 12 ? 5 : 4
    const tickIncrement = duration / (tickCount - 1)
    const ticks = [...Array(tickCount).keys()].map(
      (i) => from + i * tickIncrement
    )

    const {
      site: { tanks },
    } = this.props

    let historicalDataGasFlow: HistoricalDataEntry[] = []
    historicalDataGasFlow = this.props.data

    const dataflow = historicalDataGasFlow
      .filter((d) => !d.invalidData)
      .map((d) => ({
        ...d,
      }))

    return (
      <LineChart
        {...this.state}
        data={dataflow}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={this.isAdmin ? this.handleClick : undefined}
      >
        {this.renderThresholds(thresholds)}

        <Legend
          layout="horizontal"
          verticalAlign="top"
          align="right"
          wrapperStyle={{ marginTop: 0 }}
        />
        <Line
          name="1 hour rate of change"
          data={dataflow}
          type="monotone"
          dataKey="rateOfChangeValue"
          stroke={theme.palette.primary.main}
          strokeOpacity={
            legendState === LegendState.BringToFront ? 0.3 : undefined
          }
          strokeWidth={2}
          isAnimationActive={false}
          isUpdateAnimationActive={true}
          activeDot={(props: DotProps) => {
            const [min, max] = this.xAxisDomain
            const timestamp = props?.payload?.timestamp

            return timestamp >= min && timestamp <= max ? (
              <Dot {...props} />
            ) : (
              false
            )
          }}
          dot={(props: DotProps) => {
            const { cx, cy, value, payload } = props

            let matchingAlert = null

            if (payload?.alert?.thresholdsType === this.props.thresholdsType) {
              matchingAlert = payload.alert
            } else if (payload?.alerts) {
              matchingAlert = payload.alerts.find(
                (alert: TelemetryAlertViewModel) =>
                  alert.thresholdsType === this.props.thresholdsType
              )
            }

            if (matchingAlert) {
              return (
                <CustomizedDot
                  cx={cx}
                  cy={cy}
                  value={value}
                  alert={matchingAlert}
                />
              )
            }

            return <Dot {...props} />
          }}
        />
        <Tooltip
          content={this.renderTooltipContent}
          cursor={this.state.showTooltipCursor}
        />

        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          key={`${from.toString()} ${to.toString()}`}
          dataKey="timestamp"
          type="number"
          tickFormatter={this.toTime}
          domain={xAxisDomain}
          allowDataOverflow={!false}
          ticks={ticks}
        />
        <YAxis
          key={`${from.toString()}y ${to.toString()}y`}
          domain={yAxisDomain}
          allowDecimals={false}
          allowDataOverflow={false}
        />
      </LineChart>
    )
  }

  private renderLegend() {
    const { referenceName } = this.props

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginRight: theme.spacing(7),
          gridColumn: 1,
          gridRow: 1,
          zIndex: 1,
          cursor: 'pointer',
        }}
      >
        <MuiTooltip title="Show/bring to front/hide" placement="top">
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              opacity:
                this.state.legendState === LegendState.Hide ? 0.5 : undefined,
              cursor: 'hand',
              userSelect: 'none',
            }}
            onClick={this.onLegendClick}
          >
            <svg viewBox="0 0 15 4" width="15px">
              <line
                x1={0}
                y1={0}
                x2={15}
                y2={0}
                strokeWidth={4}
                stroke={referenceColor}
              />
            </svg>
            &nbsp;
            <Typography
              variant="body2"
              style={{
                color: referenceColor,
                fontWeight:
                  this.state.legendState === LegendState.BringToFront
                    ? 'bold'
                    : undefined,
              }}
            >
              {referenceName}
            </Typography>
          </div>
        </MuiTooltip>
      </div>
    )
  }

  private renderThresholds(statusRanges: StatusRangeResponse[]) {
    return statusRanges.map((item) => (
      <ReferenceArea
        key={`${item.minThreshold}-${item.maxThreshold}`}
        y1={item.minThreshold}
        y2={item.maxThreshold}
        fill={getStatusLensColor(item.status)}
        fillOpacity={0.3}
        ifOverflow="hidden"
      />
    ))
  }

  private readonly onLegendClick = () => {
    const states = [
      LegendState.Show,
      LegendState.BringToFront,
      LegendState.Hide,
    ]
    const index = states.findIndex((s) => this.state.legendState === s) + 1
    const legendState = states[index % states.length]

    this.setState({ legendState })
  }

  private get xAxisDomain() {
    const [from, to] = getTimeRange(this.props.timeRange)
    const domain: [number, number] = [
      (from || new Date(0)).valueOf(),
      to ? to.valueOf() : moment().valueOf(),
    ]
    return domain
  }

  private readonly toTime = (timestamp) => {
    const time = moment(timestamp)
    return time.format(`MMM D, LT`)
  }

  private readonly toTimeWithSeconds = (timestamp) => {
    const time = moment(timestamp)
    return time.format(`MMM D, LTS`)
  }

  private readonly renderTooltipContent = (props: TooltipProps) => {
    const { payload } = props

    if (!payload || !payload.length) {
      return null
    }
    const item = payload.find((p) => p.dataKey === 'rateOfChangeValue')
    if (!item) {
      return null
    }

    const { timestamp }: HistoricalDataEntry = item.payload

    const value: number = item.payload.rateOfChangeValue ?? 0
    const [min, max] = this.xAxisDomain
    let unitOfMeasure: string = item.payload.unitOfMeasure

    if (timestamp < min || timestamp > max) {
      return null
    }

    if (typeof unitOfMeasure === 'undefined') {
      unitOfMeasure = ''
    }

    let formattedValue = ''

    if (item.payload.telemetryType === TelemetryType.TankLevel) {
      const tank = this.props.site?.tanks.find(
        (t) => t.id === this.props.asset?.id
      )

      if (!tank) {
        return ''
      }

      if (tank.productionCode !== ProductionCode.Chemical) {
        const feet = inchToFeet(value)

        formattedValue = feetToBbl(feet, tank?.height, tank?.capacity).toFixed(
          2
        )
      } else {
        formattedValue = value.toFixed(2) + ' GAL/HR'
      }
    } else {
      formattedValue = value.toFixed(2)
    }

    const formattedTimestamp = this.toTimeWithSeconds(timestamp)
    const color = theme.palette.primary.main
    return (
      <div
        style={{
          zIndex: -9999,
          maxWidth: 400,
          color,
          backgroundColor: item.fill,
          border: `1px solid ${color}`,
          borderRadius: theme.spacing(0.5),
          padding: theme.spacing(0.5),
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        Rate of Change: {formattedValue}
        <i>{formattedTimestamp}</i>
        {(item.payload?.alert &&
          Object.keys(item.payload.alert).length > 0 &&
          item.payload.alert.thresholdsType === this.props.thresholdsType) ||
          (item.payload?.alerts &&
            item.payload.alerts.some(
              (a: TelemetryAlertViewModel) =>
                a.thresholdsType === this.props.thresholdsType
            ) && (
              <>
                {(() => {
                  const relevantAlert =
                    item.payload.alert?.thresholdsType ===
                    this.props.thresholdsType
                      ? item.payload.alert
                      : item.payload.alerts?.find(
                          (a: TelemetryAlertViewModel) =>
                            a.thresholdsType === this.props.thresholdsType
                        )

                  return relevantAlert?.userEmail ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        padding: '8px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '4px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      <div style={{ fontWeight: 'bold', color: '#4caf50' }}>
                        Acknowledged: Yes
                      </div>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>By:</span>{' '}
                        {relevantAlert.userEmail}
                      </div>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>Time:</span>{' '}
                        {this.toTimeWithSeconds(
                          relevantAlert.timestamp,
                          true
                        ) || 'N/A'}
                      </div>
                      <div>
                        <span
                          style={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                        >
                          Comments:
                        </span>{' '}
                        {getWords(relevantAlert.note || 'N/A')}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontWeight: 'bold', color: '#f44336' }}>
                      Acknowledged: No
                    </div>
                  )
                })()}
              </>
            ))}
      </div>
    )
  }
}

function getWords(text, many = 10, maxChars = 60) {
  if (!text) return ''

  if (text.length > maxChars) {
    return `${text.slice(0, maxChars)}...`
  }

  const words = text.split(' ')
  if (words.length <= many) {
    return text
  }

  return `${words.slice(0, many).join(' ')}...`
}

interface CustomizedDotProps {
  cx: number
  cy: number
  value: number
  alert?: TelemetryAlertViewModel
}

const CustomizedDot: React.FC<CustomizedDotProps> = ({
  cx,
  cy,
  value,
  alert,
}) => {
  if (!alert || Object.keys(alert).length === 0) {
    return (
      <svg x={cx - 5} y={cy - 5} width={10} height={10} viewBox="0 0 10 10">
        <circle
          cx="5"
          cy="5"
          r="3"
          fill="#FFFFFF"
          stroke="#f27a54"
          strokeWidth="2"
        />
      </svg>
    )
  }

  if (!alert.userEmail) {
    return (
      <svg
        x={cx - 10}
        y={cy - 10}
        width={20}
        height={20}
        viewBox="0 0 1024 1024"
      >
        <path
          d="M512 64L64 960h896L512 64z"
          fill="#FAC710"
          stroke="#000"
          strokeWidth="20"
        />
        <path
          d="M512 768c-17.6 0-32-14.4-32-32V416c0-17.6 14.4-32 32-32s32 14.4 32 32v320c0 17.6-14.4 32-32 32z"
          fill="#000"
        />
        <circle cx="512" cy="832" r="32" fill="#000" />
      </svg>
    )
  }

  return (
    <svg x={cx - 10} y={cy - 10} width={20} height={20} viewBox="0 0 1024 1024">
      <circle cx="512" cy="512" r="512" fill="green" />
      <path
        d="M768 352L448 672l-192-192"
        stroke="#fff"
        strokeWidth="80"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const mapStateToProps = (state: AppState) => ({
  user: state.multitenantUser, // state.oidc.user,
  accessToken: state.multitenantUser.accessToken, // user?.access_token,
  tenantId: state.multitenantUser.tenants?.find((t) => t.selected)?.id ?? 0,
  email: state.multitenantUser.email,
})

export default connect(mapStateToProps)(withStyles(styles)(SensorChartRates))
